import { apiRequest } from './apiClient.js';
import { tenantBasePath } from './tenant.js';

/** Mirrors apps/api/src/auth/auth.controller.ts response shapes. */
export type LoginResult =
  | {
      readonly status: 'authenticated';
      readonly tenantId: string;
      readonly principalId: string;
      readonly sessionId: string;
      readonly redirectTo: string;
      readonly csrf: CsrfProofs;
    }
  | {
      readonly status: 'mfa_required';
      readonly preSessionId: string;
      readonly challengeId: string;
      readonly redirectTo?: string;
    };

export interface CsrfProofs {
  readonly refresh: string;
  readonly logout: string;
  readonly [key: string]: string;
}

const CSRF_HEADER_NAME = 'x-csrf-proof';

/**
 * In-memory only — cleared on reload, which is fine: a reload re-runs the
 * refresh flow (cookie-based) and gets fresh proofs. Never persisted to
 * localStorage/sessionStorage.
 */
let csrfProofs: CsrfProofs | null = null;

export function login(
  slug: string,
  loginIdentifier: string,
  password: string,
): Promise<LoginResult> {
  return apiRequest<LoginResult>(`${tenantBasePath(slug)}/api/auth/session`, {
    method: 'POST',
    body: { loginIdentifier, password },
  }).then((result) => {
    if (result.status === 'authenticated') csrfProofs = result.csrf;
    return result;
  });
}

/**
 * Directly authenticates to the demo tenant with full-access privileges without
 * credentials.
 */
export function loginDemo(slug: string = 'demo'): Promise<LoginResult> {
  return apiRequest<LoginResult>(`${tenantBasePath(slug)}/api/auth/session/demo`, {
    method: 'POST',
    headers: {
      'idempotency-key': crypto.randomUUID(),
    },
    body: {},
  }).then((result) => {
    if (result.status === 'authenticated') csrfProofs = result.csrf;
    return result;
  });
}

/**
 * Completes the MFA step of `login()`'s `mfa_required` result. Backed by the
 * real `POST /t/:slug/api/auth/session/mfa` route
 * (apps/api/src/auth/auth.controller.ts) — always resolves `authenticated`
 * on success, never `mfa_required` again.
 */
export function verifyMfaLogin(
  slug: string,
  preSessionId: string,
  challengeId: string,
  otp: string,
): Promise<LoginResult> {
  return apiRequest<LoginResult>(`${tenantBasePath(slug)}/api/auth/session/mfa`, {
    method: 'POST',
    body: { preSessionId, challengeId, otp },
  }).then((result) => {
    if (result.status === 'authenticated') csrfProofs = result.csrf;
    return result;
  });
}

/**
 * Tenant login password recovery by SMS code — mirrors
 * apps/api/src/auth/auth-recovery.controller.ts. `flowId` is generated here
 * and names one recovery attempt; the request step answers `accepted` for
 * every well-formed number, registered or not.
 */
export function requestPasswordRecoveryCode(
  slug: string,
  flowId: string,
  loginIdentifier: string,
): Promise<{ readonly status: 'accepted' }> {
  return apiRequest(`${tenantBasePath(slug)}/api/auth/session/recovery`, {
    method: 'POST',
    body: { flowId, loginIdentifier },
  });
}

export function verifyPasswordRecoveryCode(
  slug: string,
  flowId: string,
  otp: string,
): Promise<{ readonly status: 'verified'; readonly resetToken: string }> {
  return apiRequest(`${tenantBasePath(slug)}/api/auth/session/recovery/verify`, {
    method: 'POST',
    body: { flowId, otp },
  });
}

export function completePasswordRecovery(
  slug: string,
  resetToken: string,
  newPassword: string,
  idempotencyKey: string,
): Promise<{ readonly status: 'password_reset'; readonly redirectTo: string }> {
  return apiRequest(`${tenantBasePath(slug)}/api/auth/session/recovery/complete`, {
    method: 'POST',
    headers: { 'idempotency-key': idempotencyKey },
    body: { resetToken, newPassword },
  });
}

/**
 * CSRF proofs live only in page memory, so a reloaded tab (or one idle past the
 * proof's 10-minute life) has none or a stale one and the server refuses its
 * refresh. Fetch fresh ones with the refresh cookie before every refresh.
 */
async function ensureRefreshProof(slug: string): Promise<void> {
  // Always fetch: a proof is single-use and expires after 10 minutes, so one
  // held in memory across an idle period or sleeping laptop is likely stale.
  try {
    const result = await apiRequest<{ csrf: CsrfProofs }>(
      `${tenantBasePath(slug)}/api/auth/session/csrf`,
      { method: 'GET' },
    );
    csrfProofs = result.csrf;
  } catch {
    // refresh below fails with the real error and the caller handles it.
  }
}

export async function refreshSession(slug: string): Promise<LoginResult> {
  await ensureRefreshProof(slug);
  const result = await apiRequest<LoginResult>(`${tenantBasePath(slug)}/api/auth/session/refresh`, {
    method: 'POST',
    headers: csrfProofs ? { [CSRF_HEADER_NAME]: csrfProofs.refresh } : {},
  });
  if (result.status === 'authenticated') csrfProofs = result.csrf;
  return result;
}

/**
 * True once `login()`/`verifyMfaLogin()` has resolved a `password_change`-
 * scoped session in this tab — the only source of the `passwordChange` CSRF
 * proof `changePassword()` needs. A page reached without it (a stale reload,
 * a direct link) has no way to obtain one except by logging in again — by
 * design, D-020: `auth.password_change` consumes a still-temporary
 * credential exactly once, so there is nothing to "recover" here.
 */
export function hasPendingPasswordChange(): boolean {
  return typeof csrfProofs?.passwordChange === 'string';
}

/**
 * ERP-245/246 — sets a real password in place of the temporary one an SMS
 * invitation issued, consuming the `password_change`-scoped session
 * `login()` returned. Backed by the real `POST /t/:slug/api/auth/session/
 * password` route (apps/api/src/auth/auth.controller.ts) — always resolves
 * `authenticated` with a `full`-scope session on success.
 *
 * `idempotencyKey` is the caller's own (a stable per-page-load value, not a
 * fresh one per call) — this route is not in `main.ts`'s idempotency-exempt
 * list, and D-020-6 makes a repeat of the exact same request a safe replay,
 * not a second consumption of the one-time credential.
 */
export function changePassword(
  slug: string,
  newPassword: string,
  idempotencyKey: string,
): Promise<LoginResult> {
  const proof = csrfProofs?.passwordChange;
  if (proof === undefined) {
    return Promise.reject(new Error('no pending password-change session in this tab'));
  }
  return apiRequest<LoginResult>(`${tenantBasePath(slug)}/api/auth/session/password`, {
    method: 'POST',
    headers: { [CSRF_HEADER_NAME]: proof, 'idempotency-key': idempotencyKey },
    body: { newPassword },
  }).then((result) => {
    if (result.status === 'authenticated') csrfProofs = result.csrf;
    return result;
  });
}

export async function logout(slug: string): Promise<void> {
  await apiRequest(`${tenantBasePath(slug)}/api/auth/session/logout`, {
    method: 'POST',
    headers: csrfProofs ? { [CSRF_HEADER_NAME]: csrfProofs.logout } : {},
  });
  csrfProofs = null;
}
